/**
 * NMS Buy Now Component
 *
 * Handles Buy Now button functionality with size selection modal integration.
 * Opens size popup when needed, then proceeds to GoKwik checkout with selected variant.
 *
 * Follows NMS Component Instance Architecture:
 * - Extends NMSBaseClass
 * - Each button gets its own component instance
 * - Uses window.neemans namespace for global registry
 * - Scoped element queries to this.element
 *
 * @extends NMSBaseClass
 */

class NMSBuyNow extends window.NMSBaseClass {
  #isInitialized = false;
  #isProcessing = false;
  #productData = null;
  #hasSize = false;
  #lastClickEvent = null;

  constructor(element) {
    super();

    // REQUIRED: Store element reference
    this.element = element;

    // REQUIRED: Unique identifier from data attributes
    this.buttonId = element.dataset.buttonId || `buy-now-${Date.now()}`;
    this.instanceId = `${this.buttonId}-${Date.now()}`;
    this.formId = element.dataset.formId;
    this.productId = null;
    this.sectionId = null;

    // REQUIRED: Store in BOTH registries for proper tracking
    window.neemans.buyNow = window.neemans.buyNow || {};
    window.neemans.buyNow[this.buttonId] = this;

    // Store in instances registry for proper cleanup
    window.neemans.buyNowInstances = window.neemans.buyNowInstances || {};
    window.neemans.buyNowInstances[this.instanceId] = this;

    // Bind methods
    this.handleBuyNowClick = this.handleBuyNowClick.bind(this);
    this.handleSizeSelection = this.handleSizeSelection.bind(this);
  }

  init() {
    if (this.#isInitialized) return;

    super.init();

    try {
      this.#setupBuyNowRefs();
      this.#parseProductData();
      this.#checkSizeOptions();
      this.#bindBuyNowEvents();
      this.#isInitialized = true;
    } catch (error) {
      this.handleError(error, 'Failed to initialize Buy Now button');
    }
  }

  /**
   * Refresh element references (called after DOM updates)
   * @public
   */
  refreshRefs() {
    try {
      this.#setupBuyNowRefs();
    } catch (error) {
      console.error('NMSBuyNow: Failed to refresh refs:', error);
    }
  }

  // REQUIRED: Scope all setup to this.element
  #setupBuyNowRefs() {
    // Get the associated product form
    this.refs.form = document.getElementById(this.formId);
    if (!this.refs.form) {
      throw new Error(`Form with ID "${this.formId}" not found`);
    }

    // Get variant and quantity inputs from form
    this.refs.variantInput = this.refs.form.querySelector('input[name="id"]');
    this.refs.quantityInput =
      this.refs.form.querySelector('input[name="quantity"]') || document.querySelector('input[name="quantity"]');

    // Get product ID and section ID from PDP size integration container
    const pdpContainer = document.querySelector('[data-nms-pdp-size-integration]');
    if (pdpContainer) {
      this.productId = pdpContainer.dataset.productId;
      this.sectionId = pdpContainer.dataset.sectionId;
    }

    // Get GST property inputs for tax calculations
    this.refs.gstRateInput = this.refs.form.querySelector('input[name="properties[gst_rate_reduction]"]');
    this.refs.originalPriceInput = this.refs.form.querySelector('input[name="properties[original_price]"]');
  }

  #parseProductData() {
    // Look for product data in PDP size integration container
    const dataScript = document.querySelector('[data-pdp-size-data]');

    if (!dataScript) {
      return;
    }

    try {
      this.#productData = JSON.parse(dataScript.textContent);

      if (!this.#productData || !this.#productData.variants) {
        throw new Error('Invalid product data structure');
      }
    } catch (error) {
      this.handleError(error, 'Failed to parse product data');
    }
  }

  #checkSizeOptions() {
    if (!this.#productData || !this.#productData.options) {
      this.#hasSize = false;
      return;
    }

    // Check if product has size-related options
    const sizeLabels = ['size', 'sizes', 'length', 'width', 'shoe size', 'clothing size', 'shirt size'];

    this.#hasSize = this.#productData.options.some(option => sizeLabels.includes(option.name.toLowerCase()));
  }

  #bindBuyNowEvents() {
    // Bind click event to buy now button
    this.element.addEventListener('click', this.handleBuyNowClick);
  }

  /**
   * Handle buy now button click
   * Checks if size selection is needed, opens modal if required, or proceeds to checkout
   * @param {Event} event - Click event
   */
  handleBuyNowClick(event) {
    event.preventDefault();

    if (this.#isProcessing) {
      return;
    }

    // Set processing flag immediately to prevent multiple clicks
    this.#isProcessing = true;

    // Store the original event for GoKwik
    this.#lastClickEvent = event;

    try {
      // Refresh refs to ensure we have the latest form state
      // This is important for sticky bar buttons that might be clicked after variant changes
      this.refreshRefs();

      // If product has no size options, proceed directly to checkout
      if (!this.#hasSize) {
        this.#proceedToCheckout();
        return;
      }

      // Check if size is already selected
      if (this.#isSizeSelected()) {
        // Size already selected, proceed to checkout
        this.#proceedToCheckout();
      } else {
        // No size selected, open size popup
        this.#isProcessing = false; // Reset since we're just opening popup
        this.#openSizePopup();
      }
    } catch (error) {
      this.#isProcessing = false;
      this.handleError(error, 'Buy Now failed');
    }
  }

  /**
   * Check if size is already selected in form
   * @returns {boolean} True if variant is selected
   * @private
   */
  #isSizeSelected() {
    const variantId = this.refs.variantInput?.value;
    return variantId && variantId !== '' && variantId !== 'undefined';
  }

  /**
   * Open size selection popup using shared popup pattern
   * @private
   */
  #openSizePopup() {
    // Initialize shared popup storage
    window.neemans.sharedSizePopups = window.neemans.sharedSizePopups || {};

    // Check if shared popup exists for this product
    let sharedPopup = window.neemans.sharedSizePopups[this.productId];

    // Create shared popup if it doesn't exist
    if (!sharedPopup && window.NMSSizePopup) {
      sharedPopup = new NMSSizePopup({
        productId: this.productId,
        productData: this.#productData,
        triggerElement: this.element,
        containerElement: document.body,
        onSelect: this.handleSizeSelection,
        mode: 'buy-now', // Special mode for buy now
        onClose: () => {
          this.#hideOverlay();
        },
      });
      sharedPopup.init();

      // Store in shared registry
      window.neemans.sharedSizePopups[this.productId] = sharedPopup;
    }

    // Update product data and show popup
    if (sharedPopup) {
      sharedPopup.updateProductData(this.#productData);
      sharedPopup.show();
    }

    // Show overlay
    this.#showOverlay();
  }

  /**
   * Show overlay backdrop
   * @private
   */
  #showOverlay() {
    const overlay = document.querySelector('.nms-overlay');
    if (overlay) {
      overlay.classList.add('nms-overlay--size-popup-active');
    }
  }

  /**
   * Hide overlay backdrop
   * @private
   */
  #hideOverlay() {
    const overlay = document.querySelector('.nms-overlay');
    if (overlay) {
      overlay.classList.remove('nms-overlay--size-popup-active');
    }
  }

  /**
   * Handle size selection from popup
   * @param {Object} selection - Size selection data from popup
   */
  handleSizeSelection(selection) {
    if (this.#isProcessing) {
      return;
    }

    this.#isProcessing = true;

    try {
      // Find matching variant with selected size
      const matchingVariant = this.#findVariantWithSize(selection);

      if (matchingVariant && matchingVariant.available) {
        // Update form with selected variant
        this.#updateVariantInForm(matchingVariant);

        // Brief delay to show UI update, then proceed to checkout
        setTimeout(() => {
          this.#proceedToCheckout();
        }, 600);
      } else {
        console.error('NMSBuyNow: Selected variant not available');
        this.#isProcessing = false;
      }
    } catch (error) {
      this.handleError(error, 'Size selection failed');
      this.#isProcessing = false;
    }
  }

  /**
   * Find variant matching size selection
   * @param {Object} selection - Size selection from popup
   * @returns {Object|null} Matching variant or null
   * @private
   */
  #findVariantWithSize(selection) {
    if (!this.#productData || !this.#productData.variants) {
      return null;
    }

    return this.#productData.variants.find(variant => {
      const optionKey = `option${selection.optionPosition}`;
      return variant[optionKey] === selection.size && variant.available;
    });
  }

  /**
   * Update form with selected variant
   * @param {Object} variant - Selected variant
   * @private
   */
  #updateVariantInForm(variant) {
    if (this.refs.variantInput) {
      this.refs.variantInput.value = variant.id;
    }

    // Update variant picker UI to show selected size
    this.#updateVariantPickerUI(variant);

    // Dispatch variant change event for other components
    this.dispatchNMSEvent('variant-changed', {
      variant: variant,
      source: 'buy-now-size-popup',
      buttonId: this.buttonId,
    });

    // Also dispatch theme's variant:change event for compatibility
    const variantChangeEvent = new CustomEvent('variant:change', {
      bubbles: true,
      detail: {
        variant: variant,
        source: 'buy-now-size-popup',
      },
    });

    document.dispatchEvent(variantChangeEvent);
  }

  /**
   * Update variant picker UI to reflect selected size
   * @param {Object} variant - Selected variant
   * @private
   */
  #updateVariantPickerUI(variant) {
    try {
      const formElement = this.refs.form;
      if (!formElement) return;

      // Find the size option that matches the selected variant
      const sizeLabels = ['size', 'sizes', 'length', 'width', 'shoe size', 'clothing size', 'shirt size'];

      // Get the size value from the variant (e.g., "UK 8" -> "8")
      const selectedSize = variant.option1 || variant.title;
      const cleanSize = selectedSize.replace('UK ', '').replace('US ', '').replace('EU ', '');

      // Find all size option inputs in the document (not just in form)
      const optionInputs = document.querySelectorAll(`input[data-option-position][form="${this.formId}"]`);

      for (const input of optionInputs) {
        const fieldset = input.closest('[data-picker-id]');
        const legend = fieldset?.querySelector('legend');
        const optionName = legend?.textContent?.toLowerCase() || '';

        // Check if this is a size option
        if (sizeLabels.some(label => optionName.includes(label))) {
          const label = input.nextElementSibling || input.closest('label');
          const inputText = label?.textContent?.trim() || '';

          // Check if this input matches the selected size
          if (inputText === cleanSize || inputText === selectedSize) {
            // Select this input
            input.checked = true;
            input.dispatchEvent(new Event('change', { bubbles: true }));

            // Update visual states
            if (label && label.classList.contains('block-swatch')) {
              // Remove is-selected from all other swatches in this option group
              const allSwatches = fieldset.querySelectorAll('.block-swatch');
              allSwatches.forEach(swatch => swatch.classList.remove('is-selected'));

              // Add is-selected to this swatch
              label.classList.add('is-selected');
            }

            // Update the display value
            const valueSpan = fieldset?.querySelector('.nms-variant__value');
            if (valueSpan) {
              valueSpan.innerHTML = inputText; // Use innerHTML to replace placeholder
            }

            break;
          }
        }
      }
    } catch (error) {
      console.error('NMSBuyNow: Error updating variant picker UI:', error);
    }
  }

  /**
   * Proceed to GoKwik checkout
   * @private
   */
  #proceedToCheckout() {
    try {
      // Get selected variant ID
      const variantId = this.#getSelectedVariantId();
      if (!variantId) {
        console.error('NMSBuyNow: No variant selected');
        this.#isProcessing = false;
        return;
      }

      // Get quantity
      const quantity = this.#getQuantity();

      // Get GST properties
      const properties = this.#getGSTProperties();

      // Call GoKwik buy now function
      this.#callGoKwikBuyNow(variantId, quantity, properties);
    } catch (error) {
      this.handleError(error, 'Checkout failed');
    } finally {
      this.#isProcessing = false;
    }
  }

  /**
   * Get selected variant ID from form
   * @returns {string|null} Variant ID or null
   * @private
   */
  #getSelectedVariantId() {
    // Priority 1: Form hidden input
    if (this.refs.variantInput && this.refs.variantInput.value) {
      return this.refs.variantInput.value;
    }

    // Priority 2: Shopify analytics
    if (window.ShopifyAnalytics?.meta?.selectedVariantId) {
      return window.ShopifyAnalytics.meta.selectedVariantId;
    }

    // Priority 3: First available variant from product data
    if (this.#productData && this.#productData.variants) {
      const availableVariant = this.#productData.variants.find(v => v.available);
      if (availableVariant) {
        return availableVariant.id;
      }
    }

    return null;
  }

  /**
   * Get quantity from input or default to 1
   * @returns {number} Quantity
   * @private
   */
  #getQuantity() {
    if (this.refs.quantityInput && this.refs.quantityInput.value) {
      return parseInt(this.refs.quantityInput.value, 10) || 1;
    }
    return 1;
  }

  /**
   * Get GST properties from product form
   * @returns {Object} GST properties object
   * @private
   */
  #getGSTProperties() {
    const properties = {};

    // Get GST rate reduction (tax benefit)
    if (this.refs.gstRateInput && this.refs.gstRateInput.value) {
      properties.gst_rate_reduction = this.refs.gstRateInput.value;
    }

    // Get original price (compare_at_price)
    if (this.refs.originalPriceInput && this.refs.originalPriceInput.value) {
      properties.original_price = this.refs.originalPriceInput.value;
    }

    return properties;
  }

  /**
   * Call GoKwik onBuyNowClick function
   * @param {string} variantId - Variant ID
   * @param {number} quantity - Quantity
   * @param {Object} properties - Custom properties (GST data)
   * @private
   */
  #callGoKwikBuyNow(variantId, quantity, properties) {
    // Global lock to prevent multiple simultaneous GoKwik calls
    if (window.nmsGoKwikProcessing) {
      this.#isProcessing = false;
      return;
    }

    // Wait for GoKwik to be loaded
    if (typeof onBuyNowClick !== 'function') {
      setTimeout(() => {
        if (typeof onBuyNowClick === 'function') {
          this.#callGoKwikBuyNow(variantId, quantity, properties);
        } else {
          console.error('NMSBuyNow: onBuyNowClick function not available');
          this.#isProcessing = false;
        }
      }, 100);
      return;
    }

    try {
      // Set global lock
      window.nmsGoKwikProcessing = true;

      // Call GoKwik function with selected variant and properties
      // Use the original click event (not synthetic) so GoKwik can access the button element
      // Only pass the currently selected item (not cart items)
      const eventToPass = this.#lastClickEvent || new Event('click');
      onBuyNowClick(eventToPass, false, [
        {
          id: variantId,
          quantity: quantity,
          properties: properties,
        },
      ]);

      // Release global lock after a delay (GoKwik should have opened by then)
      setTimeout(() => {
        window.nmsGoKwikProcessing = false;
      }, 1000);

      // Dispatch custom event for tracking
      this.dispatchNMSEvent('buy-now-clicked', {
        variantId: variantId,
        quantity: quantity,
        properties: properties,
        buttonId: this.buttonId,
      });
    } catch (error) {
      window.nmsGoKwikProcessing = false;
      this.handleError(error, 'GoKwik checkout failed');
    }
  }

  /**
   * Public API method to trigger buy now programmatically
   * @param {string|null} variantId - Optional variant ID
   * @param {number|null} quantity - Optional quantity
   * @returns {boolean} True if triggered successfully
   */
  triggerBuyNow(variantId = null, quantity = null) {
    if (this.#isProcessing) {
      return false;
    }

    // If size selection is required and no variant provided, open popup
    if (this.#hasSize && !variantId && !this.#isSizeSelected()) {
      this.#openSizePopup();
      return true;
    }

    // Set variant if provided
    if (variantId && this.refs.variantInput) {
      this.refs.variantInput.value = variantId;
    }

    // Set quantity if provided
    if (quantity && this.refs.quantityInput) {
      this.refs.quantityInput.value = quantity;
    }

    // Proceed to checkout
    this.#proceedToCheckout();
    return true;
  }

  /**
   * Clean up component
   */
  destroy() {
    // Remove event listeners
    if (this.element) {
      this.element.removeEventListener('click', this.handleBuyNowClick);
      // Remove the data-initialized attribute so it can be re-initialized if needed
      this.element.removeAttribute('data-initialized');
    }

    // NOTE: Don't destroy shared popup here - it's shared across all Buy Now instances
    // The shared popup will be destroyed when ALL instances are destroyed (see color-content-updated handler)

    // Clean up global references
    if (window.neemans.buyNow?.[this.buttonId]) {
      delete window.neemans.buyNow[this.buttonId];
    }

    if (window.neemans.buyNowInstances?.[this.instanceId]) {
      delete window.neemans.buyNowInstances[this.instanceId];
    }

    super.destroy();
    this.#isInitialized = false;
  }
}

// REQUIRED: Standard initialization pattern
function initNMSBuyNow() {
  if (!window.NMSBaseClass) {
    console.error('NMSBuyNow: NMSBaseClass required');
    return;
  }

  const elements = document.querySelectorAll('[data-nms-buy-now]:not([data-initialized])');

  for (const element of elements) {
    try {
      const instance = new NMSBuyNow(element);
      instance.init();
      element.setAttribute('data-initialized', 'true');
    } catch (error) {
      console.error('NMSBuyNow: Initialization failed:', error);
    }
  }
}

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initNMSBuyNow);
} else {
  initNMSBuyNow();
}

// Handle theme editor reloads
document.addEventListener('shopify:section:load', event => {
  if (event.target.querySelector('[data-nms-buy-now]')) {
    initNMSBuyNow();
  }
});

// Re-initialize on color content updates
document.addEventListener('nms:color-content-updated', event => {
  const { container } = event.detail;

  // Check if container has buy now buttons
  const buyNowButtons = container.querySelectorAll('[data-nms-buy-now]');
  if (buyNowButtons.length === 0) return;

  // Collect product IDs from buy now buttons in the updated container
  const productIds = new Set();
  for (const button of buyNowButtons) {
    const formId = button.dataset.formId;
    // Get product ID from associated PDP size integration container for this form
    const pdpContainer = document.querySelector(`[data-nms-pdp-size-integration][data-form-id="${formId}"]`);
    if (pdpContainer && pdpContainer.dataset.productId) {
      productIds.add(pdpContainer.dataset.productId);
    }
  }

  // Step 1: Destroy all old Buy Now instances for these products
  if (window.neemans.buyNowInstances) {
    Object.values(window.neemans.buyNowInstances).forEach(instance => {
      if (instance && productIds.has(instance.productId)) {
        instance.destroy();
      }
    });
  }

  // Step 2: Destroy shared popups for these products
  if (window.neemans.sharedSizePopups) {
    for (const productId of productIds) {
      const sharedPopup = window.neemans.sharedSizePopups[productId];
      if (sharedPopup) {
        sharedPopup.destroy();
        delete window.neemans.sharedSizePopups[productId];
      }
    }
  }

  // Step 3: Remove data-initialized attribute to allow re-initialization
  for (const button of buyNowButtons) {
    button.removeAttribute('data-initialized');
  }

  // Step 4: Initialize new instances
  initNMSBuyNow();
});

// CRITICAL: Re-initialize when product-rerender replaces buy-buttons
// The product-rerender component replaces the buy-buttons element when variants change
// This causes the Buy Now button to lose its event listeners
// We need to detect this and re-initialize
const observeBuyButtonReplacements = () => {
  // Use MutationObserver to detect when buy-buttons elements are added
  const observer = new MutationObserver(mutations => {
    let needsReinit = false;

    for (const mutation of mutations) {
      // Check if any added nodes are or contain buy-buttons
      for (const node of mutation.addedNodes) {
        if (node.nodeType === Node.ELEMENT_NODE) {
          if (node.matches && node.matches('buy-buttons')) {
            needsReinit = true;
            break;
          } else if (node.querySelector && node.querySelector('buy-buttons')) {
            needsReinit = true;
            break;
          }
        }
      }

      if (needsReinit) break;
    }

    if (needsReinit) {
      // Longer delay to ensure DOM is fully updated after product-rerender
      setTimeout(() => {
        // First, try to refresh refs on existing instances
        if (window.neemans.buyNow) {
          Object.entries(window.neemans.buyNow).forEach(([, instance]) => {
            // More robust check: look for button by form ID in current DOM
            const buttonInDOM = document.querySelector(`[data-nms-buy-now][data-form-id="${instance.formId}"]`);

            if (buttonInDOM) {
              // Button exists in DOM, refresh refs to get new form references
              // Update the element reference if it changed
              if (instance.element !== buttonInDOM) {
                instance.element = buttonInDOM;
                // Need to re-bind click handler to new button
                buttonInDOM.removeEventListener('click', instance.handleBuyNowClick);
                buttonInDOM.addEventListener('click', instance.handleBuyNowClick);
              }

              instance.refreshRefs();
            } else {
              // Button no longer exists in DOM, clean up orphaned instance
              instance.destroy();
            }
          });
        }

        // Remove data-initialized from replaced buttons so they can be re-initialized if needed
        document.querySelectorAll('[data-nms-buy-now][data-initialized]').forEach(button => {
          // Check if this button still has working event listeners
          // If it's a newly replaced button, it won't have listeners
          const hasInstance = button.dataset.buttonId && window.neemans.buyNow?.[button.dataset.buttonId];
          if (!hasInstance) {
            button.removeAttribute('data-initialized');
          }
        });

        // Initialize any new buttons
        initNMSBuyNow();
      }, 100);
    }
  });

  // Observe the document body for changes
  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });
};

// Start observing after initial load
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', observeBuyButtonReplacements);
} else {
  observeBuyButtonReplacements();
}

// Make available in global namespace
window.neemans = window.neemans || {};
window.neemans.NMSBuyNow = NMSBuyNow;
window.initNMSBuyNow = initNMSBuyNow;
